import React from 'react';
import type { GenUIResponse } from './core/parser';
import type { StreamInput } from './streaming/normalize';
import { useGenUI } from './hooks/useGenUI';
import { TextBlock } from './components/TextBlock';
import { ComponentRenderer } from './components/ComponentRenderer';
import { cn } from './utils/cn';

interface GodComponentProps {
    /** Static response (non-streaming) */
    response?: GenUIResponse;
    /** Streaming input (EventSource, ReadableStream, or Response) */
    stream?: StreamInput;
    /** Callback when an action is triggered */
    onAction?: (action: string) => void;
    /** Callback when streaming completes */
    onComplete?: () => void;
    /** Callback on error */
    onError?: (error: Error) => void;
    /** Custom class name */
    className?: string;
    /** Gap between nodes */
    gap?: 'sm' | 'md' | 'lg';
}

/**
 * GodComponent - The main entry point for rendering Gen-UI responses
 * 
 * Accepts either a static `response` or a `stream` (EventSource/ReadableStream)
 * and progressively renders text and UI components.
 */
export function GodComponent({
    response,
    stream,
    onAction,
    onComplete,
    onError,
    className,
    gap = 'md',
}: GodComponentProps) {
    const { state } = useGenUI({
        response,
        stream,
        onComplete,
        onError,
    });

    const gapStyles = {
        sm: 'space-y-2',
        md: 'space-y-4',
        lg: 'space-y-6',
    };

    if (state.error) {
        return (
            <div className={cn('rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800', className)}>
                Error: {state.error.message}
            </div>
        );
    }

    if (state.nodes.length === 0 && !state.isStreaming) {
        return null;
    }

    return (
        <div className={cn(gapStyles[gap], className)}>
            {state.nodes.map((node, index) => {
                if (node.kind === 'text') {
                    return <TextBlock key={index} content={node.content} />;
                }

                if (node.kind === 'component') {
                    return (
                        <ComponentRenderer
                            key={index}
                            component={node.component}
                            onAction={onAction}
                        />
                    );
                }

                return null;
            })}

            {state.isStreaming && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <LoadingDots />
                    <span>Generating...</span>
                </div>
            )}
        </div>
    );
}

/**
 * Simple loading dots animation
 */
function LoadingDots() {
    return (
        <div className="flex space-x-1">
            <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '0ms' }} />
            <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '150ms' }} />
            <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '300ms' }} />
        </div>
    );
}

export default GodComponent;
